/*eslint  @typescript-eslint/no-use-before-define: 0*/

import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import i18n from "@/plugins/i18n";
import { TRADE_TERM_APPLY_TO } from "@/static/tradeTermApplyToTypes";
import { ComplementAmountToAllCommand } from "@/models/api/commands/order/ComplementAmountToAllCommand";
import { ComplementAmountCommand, ComplementAmountParams } from "@/models/api/commands/order/ComplementAmountCommand";
import { AddTradeTermCommand, AddTradeTermParams } from "@/models/api/commands/order/AddTradeTermCommand";
import {
  AddTradeTermToAllCommand,
  AddTradeTermToAllParams,
} from "@/models/api/commands/order/AddTradeTermToAllCommand";
import { OrderItemPart } from "@/models/api/orders/temporary/OrderGetTemporaryOrderItems";
import { getCheckServiceTypeBeforeAddToAll } from "@/requests/order/orderAddEdit";
import { UpdateTradeTermCommand, UpdateTradeTermParams } from "@/models/api/commands/order/UpdateTradeTermCommand";

export class TradeTermModel {
  ApplyTo: TRADE_TERM_APPLY_TO = TRADE_TERM_APPLY_TO.BaseAndTaxes;
  CalculatedFrom: TRADE_TERM_APPLY_TO = TRADE_TERM_APPLY_TO.BaseAndTaxes;
  Currency = "PLN";
  IsHidden = false;
  IsPercentage = false;
  Name = "";
  PriceModifierToRootItem = false;
  Quantity = 1;
  Type = 1;
  SeparateItem = false;
  ServiceTypeId: null | number = null;
  Value = 0;
  StayOnOrder = false;
  AddOnlyPossible = false;
}

export interface TradeTermsStateInterface {
  isDialog: boolean;
  isHotelService: boolean;
  title: string;
  serviceTypeId: null | number;
  tradeTermModel: TradeTermModel;
  commandType: null | CommandType;
  orderItemId: null | string;
  priceModifierToRootItemIsVisible: boolean;
}

export const TradeTermsState: TradeTermsStateInterface = {
  isDialog: false,
  isHotelService: false,
  title: "",
  serviceTypeId: null,
  tradeTermModel: new TradeTermModel(),
  commandType: null,
  orderItemId: null,
  priceModifierToRootItemIsVisible: false,
};

export enum CommandType {
  ComplementAmount,
  ComplementAmountToAll,
  AddTradeTerm,
  AddTradeTermToAll,
  UpdateTradeTerm,
}

const TradeTermsModule = defineModule({
  state: TradeTermsState as TradeTermsStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setIsDialog(state, param: boolean) {
      state.isDialog = param;
    },
    setIsHotelService(state, param: boolean) {
      state.isHotelService = param;
    },
    setTitle(state, param: string) {
      state.title = param;
    },
    setServiceTypeId(state, param: null | number) {
      state.serviceTypeId = param;
    },
    setTradeTermModel(state, param: TradeTermModel) {
      state.tradeTermModel = param;
    },
    setCommandType(state, param: CommandType) {
      state.commandType = param;
    },
    setOrderItemId(state, param: string | null) {
      state.orderItemId = param;
    },
    setPriceModifierToRootItemIsVisible(state, param: boolean) {
      state.priceModifierToRootItemIsVisible = param;
    },
  },
  actions: {
    async openEditTradeTerm(context, param: OrderItemPart) {
      const { dispatch, commit, state } = _moduleActionContext(context);
      commit.setPriceModifierToRootItemIsVisible(false);
      await dispatch._openDialog({
        title: i18n.t("editCharge").toString(),
        serviceTypeId: param.serviceTypeId,
        orderItemId: param.id,
      });

      const model = state.tradeTermModel;
      model.ApplyTo = param.tradeTerm.applyTo;
      model.CalculatedFrom = param.tradeTerm.calculatedFrom;
      model.Currency = param.tradeTerm.currency;
      model.IsHidden = param.tradeTerm.isHidden;
      model.IsPercentage = param.tradeTerm.isPercentage;
      model.Name = param.tradeTerm.name;
      model.PriceModifierToRootItem = param.tradeTerm.priceModifierToRootItem;
      model.Quantity = param.tradeTerm.quantity;
      model.Type = param.tradeTerm.type;
      model.SeparateItem = param.tradeTerm.separateItem;
      model.ServiceTypeId = param.tradeTerm.serviceTypeId;
      model.Value = param.tradeTerm.isPercentage ? param.tradeTerm.percentValue : param.tradeTerm.value;
      model.StayOnOrder = param.tradeTerm.stayOnOrder;
      model.AddOnlyPossible = param.tradeTerm.addOnlyPossible;
      commit.setTradeTermModel(model);
      commit.setCommandType(CommandType.UpdateTradeTerm);
    },
    async openFillAll(context): Promise<void> {
      const { dispatch, commit } = _moduleActionContext(context);
      commit.setPriceModifierToRootItemIsVisible(true);
      // noinspection ES6MissingAwait
      dispatch.checkServiceTypeBeforeAddToAll();
      await dispatch._openDialog({
        title: i18n.t("fill") + " " + i18n.t("toAll"),
        serviceTypeId: null,
        orderItemId: null,
      });
      commit.setCommandType(CommandType.ComplementAmountToAll);
    },
    async openAddToAll(context): Promise<void> {
      const { dispatch, commit } = _moduleActionContext(context);
      // noinspection ES6MissingAwait
      dispatch.checkServiceTypeBeforeAddToAll();
      commit.setPriceModifierToRootItemIsVisible(true);
      await dispatch._openDialog({
        title: i18n.t("addCharge") + " " + i18n.t("toAll"),
        serviceTypeId: null,
        orderItemId: null,
      });
      commit.setCommandType(CommandType.AddTradeTermToAll);
    },
    async openAddTradeTermToItem(context, orderItem: OrderItemPart): Promise<void> {
      const { dispatch, commit } = _moduleActionContext(context);
      commit.setPriceModifierToRootItemIsVisible(false);
      await dispatch._openDialog({
        title: i18n.t("addTradeTerm").toString(),
        serviceTypeId: orderItem.serviceTypeId,
        orderItemId: orderItem.id,
      });
      commit.setIsHotelService(orderItem.serviceTypeCode.startsWith("Hotel"));
      commit.setCommandType(CommandType.AddTradeTerm);
    },
    async openComplementItem(context, orderItem: OrderItemPart): Promise<void> {
      const { dispatch, commit } = _moduleActionContext(context);
      commit.setPriceModifierToRootItemIsVisible(true);
      await dispatch._openDialog({
        title: i18n.t("fill").toString(),
        serviceTypeId: orderItem.serviceTypeId,
        orderItemId: orderItem.id,
      });
      commit.setCommandType(CommandType.ComplementAmount);
    },
    async _openDialog(
      context,
      param: { title: string; serviceTypeId: null | number; orderItemId: string | null }
    ): Promise<void> {
      const { dispatch, commit } = _moduleActionContext(context);
      commit.setIsHotelService(false);
      commit.setTradeTermModel(new TradeTermModel());
      commit.setTitle(param.title);
      commit.setIsDialog(true);
      commit.setServiceTypeId(param.serviceTypeId);
      commit.setOrderItemId(param.orderItemId);
      await dispatch.downloadItems();
    },
    async downloadItems(context): Promise<void> {
      const { rootDispatch, state, commit } = _moduleActionContext(context);
      // noinspection ES6MissingAwait
      rootDispatch.serviceTypes.downloadTradeTermTypes();
      const model = state.tradeTermModel;
      model.ServiceTypeId = await rootDispatch.serviceTypes.getCommissionServiceType(state.serviceTypeId);
      commit.setTradeTermModel(model);
    },
    async applyChanges(context): Promise<void> {
      const { rootDispatch, state, commit } = _moduleActionContext(context);

      const closeDialog = (val: unknown) => {
        if (!(val instanceof Error)) {
          commit.setIsDialog(false);
        }
      };
      if (state.commandType === CommandType.ComplementAmountToAll) {
        await rootDispatch.orderAddEdit.commands.complementAmountToAll(
          new ComplementAmountToAllCommand("", "", state.tradeTermModel, closeDialog)
        );
      } else if (state.commandType === CommandType.AddTradeTermToAll) {
        await rootDispatch.orderAddEdit.commands.addTradeTermToAll(
          new AddTradeTermToAllCommand("", "", state.tradeTermModel as AddTradeTermToAllParams, closeDialog)
        );
      } else if (state.commandType === CommandType.ComplementAmount) {
        if (state.tradeTermModel.ServiceTypeId != null)
          await rootDispatch.orderAddEdit.commands.complementAmount(
            new ComplementAmountCommand(
              "",
              "",
              {
                ...(state.tradeTermModel as unknown as ComplementAmountParams),
                ...{ OrderItemId: state.orderItemId as string },
              },
              closeDialog
            )
          );
      } else if (state.commandType === CommandType.AddTradeTerm) {
        await rootDispatch.orderAddEdit.commands.addTradeTerm(
          new AddTradeTermCommand(
            "",
            "",
            {
              ...(state.tradeTermModel as unknown as AddTradeTermParams),
              ...{ OrderItemId: state.orderItemId as string },
            },
            closeDialog
          )
        );
      } else if (state.commandType === CommandType.UpdateTradeTerm) {
        await rootDispatch.orderAddEdit.commands.updateTradeTerm(
          new UpdateTradeTermCommand(
            "",
            "",
            {
              ...(state.tradeTermModel as unknown as UpdateTradeTermParams),
              ...{ OrderItemId: state.orderItemId as string },
            },
            closeDialog
          )
        );
      }
      // commit.setIsDialog(false);
    },
    async checkServiceTypeBeforeAddToAll(context): Promise<void> {
      const { rootState } = _moduleActionContext(context);
      await getCheckServiceTypeBeforeAddToAll(rootState.orderAddEdit.operationId as string);
    },
  },
});

export default TradeTermsModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) => moduleGetterContext(args, TradeTermsModule);
const _moduleActionContext = (context: any) => moduleActionContext(context, TradeTermsModule);
